import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Collapse, IconButton, Link, Paper, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTheme } from '@mui/material/styles';

import { askAssistant, assistantUsage } from '../../../utils/assistantService';
import { styles } from '../styles/assistantStyles';

/**
 * "Ask DiscloSure" — the dashboard AI assistant (AI Assistant and Metadata Store plan, phase 3).
 * District and school admins (and DiscloSure admins once a district is picked) ask plain-English
 * questions; answers come from the read-only /portal/assistant back end. Links come from the
 * server and open the matching report in the portal.
 */
export const ASSISTANT_ROLES = ['districtAdmin', 'schoolAdmin', 'disclosureAdmin'];

// The 10 approved starting questions.
export const SUGGESTED_QUESTIONS = [
  'How many reports did we get this month compared with last month?',
  'Which schools have the most open reports per student?',
  'What are the most common report types this semester?',
  'Are there any open emergencies or Level 1–2 reports right now?',
  'Which reports have had no update in more than 5 days?',
  "What's our average time to first response, by school?",
  'Who has the most open cases assigned right now?',
  'Who is most often involved in reports this school year?',
  'What share of closed reports were marked satisfied?',
  'What days and times do most reports come in?',
];

const MAX_HISTORY = 6;

const AssistantPanel = ({ user, district }) => {
  const theme = useTheme();
  const style = styles(theme);
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState([]); // { role: 'user'|'assistant', text, links?, error? }
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const endRef = useRef(null);

  const isDisclosureAdmin = user?.permission === 'disclosureAdmin';
  const scopeDistrict = isDisclosureAdmin ? district : undefined;
  const needsDistrict = isDisclosureAdmin && !district;

  useEffect(() => {
    if (needsDistrict) return;
    assistantUsage(scopeDistrict).then((u) => {
      if (u.ok) setUsage({ used: u.used, limit: u.limit });
    });
    // A DiscloSure admin switching district starts a fresh conversation.
    setTurns([]);
    // eslint-disable-next-line
  }, [scopeDistrict]);

  useEffect(() => {
    if (endRef.current && endRef.current.scrollIntoView) {
      endRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [turns, busy]);

  if (!user || !ASSISTANT_ROLES.includes(user.permission)) return null;

  const ask = async (text) => {
    const q = (text || '').trim();
    if (!q || busy || needsDistrict) return;
    const history = turns
      .filter((t) => !t.error)
      .slice(-MAX_HISTORY)
      .map((t) => ({ role: t.role, text: t.text }));
    setTurns((prev) => [...prev, { role: 'user', text: q }]);
    setQuestion('');
    setBusy(true);
    const res = await askAssistant({ question: q, history, district: scopeDistrict });
    setBusy(false);
    if (res.ok) {
      setTurns((prev) => [...prev, { role: 'assistant', text: res.answer, links: res.links || [] }]);
      if (res.limit) setUsage({ used: res.used, limit: res.limit });
    } else {
      setTurns((prev) => [...prev, { role: 'assistant', text: res.message, error: true }]);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(question);
    }
  };

  const limitReached = usage && usage.limit && usage.used >= usage.limit;

  return (
    <Paper elevation={0} sx={style.card} data-testid="assistant-panel">
      <Box sx={style.header}>
        <Box sx={style.headerTitle}>
          <AutoAwesomeIcon sx={style.headerIcon} />
          <Box>
            <Typography sx={style.title}>Ask DiscloSure</Typography>
            <Typography sx={style.subtitle}>
              Ask about your reports in plain English. Answers use counts and statuses only, never what students wrote.
            </Typography>
          </Box>
        </Box>
        <Box sx={style.headerActions}>
          {usage && usage.limit ? (
            <Typography sx={style.usage}>{usage.used} of {usage.limit} questions this month</Typography>
          ) : null}
          {turns.length > 0 && (
            <IconButton aria-label="Start a new conversation" size="small" onClick={() => setTurns([])}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton aria-label={open ? 'Collapse assistant' : 'Expand assistant'} size="small" onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={open}>
        {needsDistrict ? (
          <Typography sx={style.hint}>Select a district above to ask questions about it.</Typography>
        ) : (
          <>
            {turns.length === 0 && (
              <Box sx={style.suggestions}>
                {SUGGESTED_QUESTIONS.map((s) => (
                  <Chip key={s} label={s} onClick={() => ask(s)} sx={style.chip} disabled={busy || limitReached} />
                ))}
              </Box>
            )}

            {turns.length > 0 && (
              <Box sx={style.thread} aria-live="polite">
                {turns.map((t, i) => (
                  <Box key={i} sx={t.role === 'user' ? style.userBubble : (t.error ? style.errorBubble : style.answerBubble)}>
                    <Typography sx={style.bubbleText}>{t.text}</Typography>
                    {t.links && t.links.length > 0 && (
                      <Box sx={style.links}>
                        {t.links.map((l) => (
                          <Link
                            key={l.path}
                            component="button"
                            type="button"
                            onClick={() => navigate(l.path)}
                            sx={style.link}
                          >
                            {l.label}
                          </Link>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
                {busy && (
                  <Box sx={style.thinking}>
                    <CircularProgress size={16} />
                    <Typography sx={style.thinkingText}>Looking that up…</Typography>
                  </Box>
                )}
                <div ref={endRef} />
              </Box>
            )}

            <Box sx={style.inputRow}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                placeholder={limitReached ? "This month's question limit has been reached." : 'Ask a question, e.g. "What needs attention right now?"'}
                value={question}
                onChange={(e) => setQuestion(e.target.value.slice(0, 1000))}
                onKeyDown={onKeyDown}
                disabled={busy || limitReached}
                inputProps={{ 'aria-label': 'Ask DiscloSure a question' }}
              />
              <Button
                variant="contained"
                onClick={() => ask(question)}
                disabled={busy || limitReached || !question.trim()}
                endIcon={<SendIcon />}
                sx={style.sendButton}
              >
                Ask
              </Button>
            </Box>
            <Typography sx={style.footnote}>
              Trends are updated nightly; "right now" questions use live report status. Check important numbers in the reports before acting.
            </Typography>
          </>
        )}
      </Collapse>
    </Paper>
  );
};

export default AssistantPanel;
